import { createContext, useContext, useMemo } from 'react';
import { GoMotoComponent } from '../../API/GoMoto.types';
import { PermissionsContextState, PermissionsProviderProps, PermissionsState, PermissionsStateContextType } from './Permissions.types';
import { useIsTablet } from '../../CommonUtils/MediaQueries';
import { useHasFullAccess, useHasViewAccess, useUserState } from '../User';
import { UserPermissions } from '../../API/Enums';

/**
 * Sets up the Permissions state context and defaults it to undefined so that if any Permissions context hook is used outside of the
 * Permissions state context, we can throw an error.
 */
const PermissionsStateContext: PermissionsStateContextType = createContext<PermissionsState>(undefined);

// #region Provider
/**
 * Provides the Permissions state and a dispatch to update the state to all components that
 * are rendered as a child node of this component.
 *
 * @param children - {@link PermissionsProviderProps children} description.
 */
export const PermissionsProvider: GoMotoComponent<PermissionsProviderProps> = ({
    children,
}) => {
    const { UserData } = useUserState();
    const hasViewAccess = useHasViewAccess();
    const hasFullAccess = useHasFullAccess();
    const isTablet: boolean = useIsTablet();

    const state: PermissionsContextState = {
        summaryAccess: useMemo<boolean>(() => {
            return !isTablet
                && (
                    hasViewAccess(UserPermissions.SummaryDashboardAccess)
                    || hasViewAccess(UserPermissions.SummaryDashboard_BrandView)
                    || hasViewAccess(UserPermissions.SummaryDashboard_CompareView)
                );
        }, [hasViewAccess, isTablet]),
        activityAccess: useMemo<boolean>(() => {
            return hasViewAccess(UserPermissions.ActivityDashboardAccess);
        }, [hasViewAccess]),
        userManagementAccess: useMemo<boolean>(() => {
            return !isTablet && hasViewAccess(UserPermissions.UserManagementAccess);
        }, [hasViewAccess, isTablet]),
        keyManagementAccess: useMemo<boolean>(() => {
            return isTablet && hasViewAccess(UserPermissions.KeyManagement_Mobile) && (UserData.is_staff || UserData.show_key_management_menu);
        }, [hasViewAccess, isTablet, UserData.is_staff, UserData.show_key_management_menu]),
        keyManagementCanEdit: useMemo<boolean>(() => {
            return hasFullAccess(UserPermissions.KeyManagement_Mobile);
        }, [hasFullAccess]),
        qrCodeCheckInAccess: useMemo<boolean>(() => {
            return isTablet && hasViewAccess(UserPermissions.QRCodeScan_Mobile) && (UserData.is_staff || UserData.show_qr_code_scan_menu);
        }, [hasViewAccess, isTablet, UserData.is_staff, UserData.show_qr_code_scan_menu]),
        settingsAccess: useMemo<boolean>(() => {
            return !isTablet && (
                hasViewAccess(UserPermissions.Settings_GeneralSettings)
                || hasViewAccess(UserPermissions.Settings_DisclaimersAndConsents)
                || hasViewAccess(UserPermissions.Settings_Communications)
            );
        }, [hasViewAccess, isTablet]),
    };

    return (
        <PermissionsStateContext.Provider value={state}>
            {children}
        </PermissionsStateContext.Provider>
    );
};
// #endregion

// #region usePermissionsState
/**
 * Permissions context hook that retrieves the state from the context, or throws
 * an error if not used in a component that is a child of the Permissions context.
 */
export const usePermissionsState = () => {
    const context: PermissionsState = useContext(PermissionsStateContext);

    if (context === undefined) {
        throw new Error('usePermissionsState must be used within a PermissionsProvider');
    }

    return context;
};
// #endregion